/**
 * prefers-reduced-motion — single source of truth for the motion system.
 *
 * CSS-driven motion is already neutralised globally (see the guard in
 * `styles/global.css` that collapses animation/transition durations to ~0).
 * JS-driven motion (springs, particle bursts) must consult these helpers and
 * jump straight to the final state instead of animating.
 */

import { type Accessor, createSignal, onCleanup } from "solid-js";

const QUERY = "(prefers-reduced-motion:reduce)";

/** One-shot, non-reactive check. Safe in non-DOM contexts (returns false). */
export function prefersReducedMotion(): boolean {
  return typeof globalThis.matchMedia === "function" && globalThis.matchMedia(QUERY).matches;
}

/**
 * Reactive accessor that tracks the media query. Cleans up with the owning
 * reactive scope; returns a constant `false` accessor in non-DOM contexts.
 */
export function createReducedMotion(): Accessor<boolean> {
  if (typeof globalThis.matchMedia !== "function") {
    return () => false;
  }
  const list = globalThis.matchMedia(QUERY);
  const [reduced, setReduced] = createSignal(list.matches);
  const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
  list.addEventListener("change", handler);
  onCleanup(() => list.removeEventListener("change", handler));
  return reduced;
}
